/**
 * ACLED conflict and protest data source.
 * Fetches armed conflict, protest, and political violence events.
 * Authenticates via OAuth (email + password → Bearer token).
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity, EventType, EntityReference } from '../base-source';
import { getApiBase } from '@/app/api-router';
import { djb2Hash } from '@/utils/hash';

const SOURCE_ID = 'acled';

function fatalitiesToSeverity(fatalities: number): Severity {
  if (fatalities >= 10) return 'critical';
  if (fatalities >= 3) return 'high';
  if (fatalities >= 1) return 'medium';
  return 'low';
}

function acledEventTypeToType(eventType: string): EventType {
  const lower = eventType.toLowerCase();
  if (lower.includes('protest')) return 'protest';
  if (lower.includes('battle') || lower.includes('violence') || lower.includes('explosion')) return 'conflict';
  return 'political';
}

interface AcledRecord {
  event_id_cnty: string;
  event_date: string;
  event_type: string;
  sub_event_type: string;
  actor1: string;
  actor2: string;
  country: string;
  admin1: string;
  location: string;
  latitude: string;
  longitude: string;
  fatalities: string;
  notes: string;
  source: string;
}

interface AcledResponse {
  status: number;
  data: AcledRecord[];
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const acledSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'ACLED',
    description: 'Armed Conflict Location & Event Data — conflict, protests, political violence',
    icon: 'crosshairs',
    category: 'conflict',
    updateInterval: 3_600_000, // 1 hour
    requiresApiKey: true,
    apiKeyUrl: 'https://acleddata.com/register/',
    dataLicense: 'ACLED Terms of Use',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    { key: 'acled_email', label: 'ACLED Email', type: 'text', required: true },
    { key: 'acled_password', label: 'ACLED Password', type: 'password', required: true },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] ACLED conflict source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Proxy handles API key injection and date range calculation
      const url = `${getApiBase()}/api/data/acled?limit=500&days=7`;

      const response = await fetch(url, { signal: AbortSignal.timeout(20_000) });

      if (!response.ok) {
        throw new Error(`ACLED API returned ${response.status}`);
      }

      const data: AcledResponse = await response.json();

      if (data.status !== 200 || !Array.isArray(data.data)) {
        return [];
      }

      const events: NormalizedEvent[] = [];

      for (const record of data.data) {
        const lat = parseFloat(record.latitude);
        const lng = parseFloat(record.longitude);
        const fatalities = parseInt(record.fatalities, 10) || 0;

        if (isNaN(lat) || isNaN(lng)) continue;

        const entities: EntityReference[] = [];
        if (record.actor1) {
          entities.push({
            entityId: `acled-actor-${djb2Hash(record.actor1)}`,
            name: record.actor1,
            type: 'actor',
            confidence: 0.9,
          });
        }
        if (record.actor2) {
          entities.push({
            entityId: `acled-actor-${djb2Hash(record.actor2)}`,
            name: record.actor2,
            type: 'actor',
            confidence: 0.9,
          });
        }

        events.push({
          id: `acled-${record.event_id_cnty}`,
          source: SOURCE_ID,
          timestamp: new Date(record.event_date).toISOString(),
          type: acledEventTypeToType(record.event_type),
          title: `${record.event_type}: ${record.sub_event_type} — ${record.country}`,
          description: record.notes || undefined,
          severity: fatalitiesToSeverity(fatalities),
          location: {
            lat,
            lng,
            country: record.country,
            name: record.admin1 || record.location || undefined,
          },
          entities: entities.length > 0 ? entities : undefined,
          tags: [record.event_type, record.sub_event_type, record.country].filter(Boolean),
          raw: {
            fatalities,
            actor1: record.actor1,
            actor2: record.actor2,
            source: record.source,
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
